/**
 * Constants for AI Prescription feature
 * Centralized location for all configuration values, timeouts, and magic strings
 */

// LocalStorage keys
export const STORAGE_KEYS = {
  PREFERRED_PHARMACY: "preferredPharmacy",
  PHARMACY_ID: "pharmacyIdFromAPI",
  PENDING_SELECTED_PHARMACY: "aiPrescriptionPendingSelectedPharmacy",
  PENDING_SELECTED_FAVOURITE_DRUG: "aiPrescriptionPendingSelectedFavouriteDrug",
  PENDING_SEND_ORIGIN: "aiPrescriptionPendingSendOrigin",
} as const;

// Timeout values (in milliseconds)
export const TIMEOUTS = {
  API_CALL_DELAY: 1500,
  SCROLL_DELAY: 400,
  SCROLL_DELAY_AFTER_SEARCH: 500,
  VOICE_SEARCH: 5000,
} as const;

// WebSocket message types
export const WS_MESSAGE_TYPES = {
  PHARMACY: "pharmacy",
  TOKEN: "token",
  RESULT: "result",
  STATUS: "status",
  ERROR: "error",
} as const;

// Intent types
export const INTENT_TYPES = {
  COMPLETE: "complete",
  SUGGESTIONS: "suggestions",
  PHARMACY_LOOKUP: "pharmacy_lookup",
  GET_FAVOURITE_PHARMACY: "get_favourite_pharmacy",
  PHARMACY_SUGGESTIONS: "pharmacy_suggestions",
  PBM_SUGGESTIONS: "pbm_suggestions",
  FAVOURITE_DRUGS_SUGGESTIONS: "favourite_drugs_suggestions",
} as const;

// Intent detection patterns (CONFIRMATION, SEND_COMMAND, CHANGE_PHARMACY managed by WSS)
export const INTENT_PATTERNS = {
  MODIFICATION: /\b(substitutions not allowed)\b/i,
} as const;

// Default values
export const DEFAULTS = {
  SUBSTITUTIONS: "Substitutions allowed",
  SUBSTITUTIONS_NOT_ALLOWED: "Substitutions not allowed",
  EMPTY_STRING: "",
} as const;

// Pharmacy field name for extracting pharmacy list from WSS response
export const PHARMACY_FIELDS = {
  PHARMACY_SUGGESTIONS: "pharmacy_suggestions",
} as const;

// Status codes
export const STATUS_CODES = {
  SUCCESS: 200,
} as const;

/**
 * Shared scrollbar sx for consistent styling across AI Prescription (results list, pharmacy list, etc.).
 * Pass primaryColor from theme; optional overrides for track/width.
 */
export const getScrollbarSx = (
  primaryColor: string,
  options?: { width?: string; trackBackground?: string; borderRadius?: string; thumbHover?: boolean }
) => ({
  "&::-webkit-scrollbar": {
    width: options?.width ?? "10px",
  },
  "&::-webkit-scrollbar-track": {
    background: options?.trackBackground ?? "#f5f5f5",
    borderRadius: options?.borderRadius ?? "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: primaryColor,
    borderRadius: options?.borderRadius ?? "8px",
    border: "2px solid #f5f5f5",
  },
  ...(options?.thumbHover && {
    "&::-webkit-scrollbar-thumb:hover": {
      background: `${primaryColor}CC`,
    },
  }),
});

/**
 * Returns initial pharmacy state after clearing localStorage keys.
 * Use for preferredPharmacy and pharmacyIdFromAPI useState initializers.
 */
export const getInitialPharmacyState = (): {
  preferredPharmacy: string;
  pharmacyIdFromAPI: null;
} => {
  try {
    localStorage.removeItem(STORAGE_KEYS.PREFERRED_PHARMACY);
    localStorage.removeItem(STORAGE_KEYS.PHARMACY_ID);
  } catch (error) {
    console.error("❌ Error initializing pharmacy state:", error);
  }
  return { preferredPharmacy: "", pharmacyIdFromAPI: null };
};

